import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput, { parsePhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import type { User, CreateUserPayload, UpdateUserPayload } from '../types/user';
import type {
  CreateHubMemberPayload,
  InvestorMemberRole,
  SetUserInvestorMembershipResult,
} from '../types/investor';
import { validateUserForm, type UserFormErrors, type UserKind } from '../utils/userFormValidation';
import HubMemberFields from './HubMemberFields';
import InvestorMembershipSection from './InvestorMembershipSection';

interface UserFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateUserPayload | UpdateUserPayload) => void | Promise<void>;
  /** Create-only: adds a new user as a member of an existing investor. */
  onSubmitHubMember: (investorId: string, payload: CreateHubMemberPayload) => void | Promise<void>;
  /** Edit-only: called after the user's investor was changed from the modal. */
  onMembershipApplied: (result: SetUserInvestorMembershipResult) => void;
  user?: User | null;
  externalErrors?: {
    name?: string;
    lastname?: string;
    email?: string;
    phone?: string;
    password?: string;
  };
}

/**
 * Formats a date string into a localized date and time format.
 * @param dateString - ISO date string to format
 * @returns Localized date and time string
 */
const formatDateTime = (dateString: string): string => {
  return new Date(dateString).toLocaleString();
};

// Keyed by UsersPage so it remounts on each open, making lazy init the only reset path.
const UserFormModal: React.FC<UserFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  onSubmitHubMember,
  onMembershipApplied,
  user,
  externalErrors,
}) => {
  const { t } = useTranslation();
  // Stable ids to tie each caption <label> and error message to its control.
  const fieldId = useId();
  const isEdit = !!user;
  // Hub mode is create-only; editing always targets the platform user record.
  const [kind, setKind] = useState<UserKind>('platform');
  const isHub = !isEdit && kind === 'hub';
  const [investorId, setInvestorId] = useState('');
  const [role, setRole] = useState<InvestorMemberRole | ''>('');

  const [name, setName] = useState(() => user?.name ?? '');
  const [lastname, setLastname] = useState(() => user?.lastname ?? '');
  const [email, setEmail] = useState(() => user?.email ?? '');
  const [phone, setPhone] = useState(() => user?.phone ?? '');
  const [isActive, setIsActive] = useState(() => user?.isActive ?? true);
  const [mustChangePassword, setMustChangePassword] = useState(
    () => user?.mustChangePassword ?? true
  );
  const [password, setPassword] = useState('');
  const [referenceCode, setReferenceCode] = useState(() => user?.referenceCode ?? '');
  // Holds i18n keys from validateUserForm; translated below.
  const [localErrors, setLocalErrors] = useState<UserFormErrors>({});

  // Merge external errors (already messages) at render time — no effect needed.
  const translatedLocal = Object.fromEntries(
    Object.entries(localErrors).map(([field, key]) => [field, t(key)])
  ) as UserFormErrors;
  const errors: UserFormErrors = { ...translatedLocal, ...externalErrors };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors = validateUserForm(
      { name, lastname, email, phone, password, investorId, role },
      { kind, isEdit }
    );
    if (Object.keys(newErrors).length) {
      setLocalErrors(newErrors);
      return;
    }
    // Valid now: drop messages from a previous failed attempt (e.g. a phone just filled in).
    setLocalErrors({});

    if (isHub && role) {
      // Empty password is omitted on purpose: the backend then sends a magic-link invite.
      await onSubmitHubMember(investorId, {
        name,
        lastname,
        email,
        role,
        password: password || undefined,
      });
      return;
    }

    // Re-derive strict E.164 from the parsed number instead of trusting the raw
    // string: legacy records (e.g. from the StartCo form) can carry a
    // "+506 87980994" value the backend's E.164 regex rejects, and it would
    // otherwise be resubmitted unchanged whenever the field isn't touched.
    const normalizedPhone = phone ? (parsePhoneNumber(phone)?.number ?? phone) : phone;

    if (isEdit && user) {
      await onSubmit({
        id: user.id,
        name,
        lastname,
        email,
        phone: normalizedPhone || undefined,
        isActive,
        mustChangePassword,
        password: password || undefined,
        referenceCode: referenceCode || undefined,
      });
    } else {
      await onSubmit({
        name,
        lastname,
        email,
        phone: normalizedPhone,
        isActive,
        mustChangePassword,
        password,
        referenceCode: referenceCode || undefined,
      });
    }
  };

  if (!open) return null;

  return (
    <div className="modal modal-open">
      <div
        className="modal-box"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${fieldId}-title`}
      >
        <h3 id={`${fieldId}-title`} className="font-bold text-lg mb-4">
          {isEdit ? t('admin:users.editTitle') : t('admin:users.createTitle')}
        </h3>
        <form onSubmit={handleSubmit}>
          {!isEdit && (
            <div className="form-control mb-4">
              <div className="grid grid-cols-[auto_1fr] items-center gap-3">
                <label htmlFor="user-kind" className="label-text">
                  {t('admin:users.kind.label')}
                </label>
                <select
                  id="user-kind"
                  className="select select-bordered w-full"
                  value={kind}
                  onChange={(e) => {
                    setKind(e.target.value as UserKind);
                    setLocalErrors({});
                  }}
                >
                  <option value="platform">{t('admin:users.kind.platform')}</option>
                  <option value="hub">{t('admin:users.kind.hub')}</option>
                </select>
              </div>
            </div>
          )}
          {isHub && (
            <HubMemberFields
              investorId={investorId}
              role={role}
              onInvestorChange={setInvestorId}
              onRoleChange={setRole}
              errors={{ investorId: errors.investorId, role: errors.role }}
            />
          )}
          <div className="form-control mb-4">
            <div className="grid grid-cols-[auto_1fr] items-center gap-3">
              <label htmlFor={`${fieldId}-name`} className="label-text">
                {t('admin:users.name')}
              </label>
              <input
                id={`${fieldId}-name`}
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? `${fieldId}-name-error` : undefined}
                className="input input-bordered w-full"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              {errors.name && (
                <span id={`${fieldId}-name-error`} className="text-error text-xs mt-1 ml-[auto]">
                  {errors.name}
                </span>
              )}
            </div>
          </div>
          <div className="form-control mb-4">
            <div className="grid grid-cols-[auto_1fr] items-center gap-3">
              <label htmlFor={`${fieldId}-lastname`} className="label-text">
                {t('admin:users.lastname')}
              </label>
              <input
                id={`${fieldId}-lastname`}
                aria-invalid={!!errors.lastname}
                aria-describedby={errors.lastname ? `${fieldId}-lastname-error` : undefined}
                className="input input-bordered w-full"
                value={lastname}
                onChange={(e) => setLastname(e.target.value)}
                required
              />
              {errors.lastname && (
                <span
                  id={`${fieldId}-lastname-error`}
                  className="text-error text-xs mt-1 ml-[auto]"
                >
                  {errors.lastname}
                </span>
              )}
            </div>
          </div>
          <div className="form-control mb-4">
            <div className="grid grid-cols-[auto_1fr] items-center gap-3">
              <label htmlFor={`${fieldId}-email`} className="label-text">
                {t('admin:users.email')}
              </label>
              <input
                id={`${fieldId}-email`}
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? `${fieldId}-email-error` : undefined}
                className="input input-bordered w-full"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              {errors.email && (
                <span id={`${fieldId}-email-error`} className="text-error text-xs mt-1 ml-[auto]">
                  {errors.email}
                </span>
              )}
            </div>
          </div>
          {/* Platform-only fields — the Hub member endpoint doesn't accept them. */}
          {!isHub && (
            <>
              <div className="form-control mb-4">
                <div className="grid grid-cols-[auto_1fr] items-center gap-3">
                  <label htmlFor={`${fieldId}-phone`} className="label-text">
                    {t('admin:users.phone')}
                  </label>
                  <PhoneInput
                    international
                    defaultCountry="US"
                    value={phone}
                    onChange={(val) => setPhone(val ?? '')}
                    className="input input-bordered w-full"
                    countrySelectProps={{ 'aria-label': t('admin:users.phoneCountry') }}
                    numberInputProps={{
                      id: `${fieldId}-phone`,
                      'aria-invalid': !!errors.phone,
                      className:
                        'bg-transparent outline-none border-none shadow-none w-full h-full',
                    }}
                  />
                  {errors.phone && (
                    <span
                      id={`${fieldId}-phone-error`}
                      className="text-error text-xs mt-1 ml-[auto]"
                    >
                      {errors.phone}
                    </span>
                  )}
                </div>
              </div>
              <div className="form-control mb-4">
                <div className="grid grid-cols-[auto_1fr] items-center gap-3">
                  <label htmlFor={`${fieldId}-isActive`} className="label-text">
                    {t('admin:users.isActive')}
                  </label>
                  <input
                    id={`${fieldId}-isActive`}
                    type="checkbox"
                    className="toggle toggle-primary"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                </div>
              </div>
              <div className="form-control mb-4">
                <div className="grid grid-cols-[auto_1fr] items-center gap-3">
                  <label htmlFor={`${fieldId}-mustChangePassword`} className="label-text">
                    {t('admin:users.mustChangePassword')}
                  </label>
                  <input
                    id={`${fieldId}-mustChangePassword`}
                    type="checkbox"
                    className="toggle toggle-warning"
                    checked={mustChangePassword}
                    onChange={(e) => setMustChangePassword(e.target.checked)}
                  />
                </div>
              </div>
              <div className="form-control mb-4">
                <div className="grid grid-cols-[auto_1fr] items-center gap-3">
                  <label htmlFor={`${fieldId}-referenceCode`} className="label-text">
                    {t('admin:users.referenceCode')}
                  </label>
                  <input
                    id={`${fieldId}-referenceCode`}
                    className="input input-bordered w-full"
                    readOnly
                    onFocus={(e) => e.currentTarget.removeAttribute('readonly')}
                    value={referenceCode}
                    onChange={(e) => setReferenceCode(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}
          {isEdit && user && (
            <>
              <div className="form-control mb-2">
                <div className="grid grid-cols-[auto_1fr] items-center gap-3">
                  <label htmlFor={`${fieldId}-createdAt`} className="label-text">
                    {t('admin:users.createdAt')}
                  </label>
                  <input
                    id={`${fieldId}-createdAt`}
                    className="input input-bordered w-full"
                    value={formatDateTime(user.createdAt)}
                    disabled
                  />
                </div>
              </div>
              <div className="form-control mb-4">
                <div className="grid grid-cols-[auto_1fr] items-center gap-3">
                  <label htmlFor={`${fieldId}-updatedAt`} className="label-text">
                    {t('admin:users.updatedAt')}
                  </label>
                  <input
                    id={`${fieldId}-updatedAt`}
                    className="input input-bordered w-full"
                    value={formatDateTime(user.updatedAt)}
                    disabled
                  />
                </div>
              </div>
            </>
          )}
          <div className="form-control mb-4">
            <div className="grid grid-cols-[auto_1fr] items-center gap-3">
              <label htmlFor={`${fieldId}-password`} className="label-text">
                {t('admin:users.password')}
              </label>
              <input
                id={`${fieldId}-password`}
                aria-invalid={!!errors.password}
                aria-describedby={errors.password ? `${fieldId}-password-error` : undefined}
                className="input input-bordered w-full"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required={!isEdit && !isHub}
                minLength={8}
              />
              {errors.password && (
                <span
                  id={`${fieldId}-password-error`}
                  className="text-error text-xs mt-1 ml-[auto]"
                >
                  {errors.password}
                </span>
              )}
            </div>
            {(isEdit || isHub) && (
              <span className="label-text-alt text-gray-500 text-xs mt-1 ml-[auto]">
                {isHub ? t('admin:users.hub.passwordHelp') : t('admin:users.passwordHelp')}
              </span>
            )}
          </div>
          {/* Investor assignment is applied on its own (has its own button/warnings). */}
          {isEdit && user && (
            <InvestorMembershipSection userId={user.id} onApplied={onMembershipApplied} />
          )}
          <div className="modal-action">
            <button type="button" className="btn" onClick={onClose}>
              {t('admin:users.cancel')}
            </button>
            <button type="submit" className="btn btn-primary ml-2">
              {t('admin:users.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserFormModal;
